<template>
  <!--
    Hidden until it can draw the plate. This layer is opaque — it replaced the
    screen blend when it took over the artwork — so painting it before the
    texture arrives would black the hero out for a frame. Until then the
    <img> underneath is what you see, which is also the permanent state when
    WebGL is unavailable.
  -->
  <canvas
    v-if="supported"
    ref="canvas"
    class="atmosphere"
    :class="{ 'atmosphere--live': ready }"
    aria-hidden="true"
  ></canvas>
</template>

<script setup>
import { onBeforeUnmount, onMounted, ref, shallowRef } from 'vue';

const props = defineProps({
  /* Where the light source sits inside the source image, in image UV. The rift
   * ring in rift-gate.webp is centred here — not at the middle of the frame. */
  focus: { type: Array, default: () => [0.465, 0.305] },
  /* The image's own aspect and its CSS object-position, so the shader can
   * reproduce the object-fit: cover crop and keep the glow on the rift at
   * every viewport shape. */
  imageAspect: { type: Number, default: 1456 / 816 },
  objectPosition: { type: Array, default: () => [0.5, 0.28] },
  intensity: { type: Number, default: 1 },
  /* The plate itself. The shader reads it to find the figures, the skyline
   * and the ground, which is the only way a strike can light real shapes
   * instead of a noise field. */
  plate: { type: String, default: '' },
  /* Background depth for the plate, near = white. Drives the parallax (when it
   * is enabled) and
   * tells the lighting which pixels are a figure and which are cloud. */
  depth: { type: String, default: '' },
  /* Furthest the nearest plane travels, in image UV. 0 disables it outright,
   * listeners and all.
   *
   * The claim that the stretching at an occlusion edge stays invisible did not
   * survive contact with a plate that has figures on it. Displacing one flat
   * painting by a depth map has no background to uncover behind a near object,
   * so its edge pixels smear across the gap — subtle on a skyline, obvious on
   * a person. The site hero therefore passes 0 and moves its figures as real
   * layers instead (HeroSection). This is kept for plates that are pure
   * landscape, where there is no silhouette close enough to give it away. */
  parallax: { type: Number, default: 0.0105 },
});

const canvas = ref(null);
const supported = ref(true);
const ready = ref(false);
const gl = shallowRef(null);

let frame = 0;
let program = null;
let plateTex = null;
let depthTex = null;

/* Where the parallax is going, and where it currently is. The gap between
 * them is the easing: a pointer that teleports the scene is a jolt, not
 * depth. */
const target = { x: 0, y: 0 };
const current = { x: 0, y: 0 };
let scrollShift = 0;
let uniforms = {};
let observer = null;
let clock = 0;
let last = 0;
let inView = true;
let visible = true;
let reduced = false;

/* Full resolution, now that this layer carries the artwork rather than a wash
 * of light over it. Anything less resamples the plate and softens the hero,
 * which is not a trade worth making for an effect. The device pixel ratio is
 * still capped at 2 — beyond that the fragment count triples for a difference
 * nobody can see on a photograph. */
let renderScale = 1;

/*
 * Adaptive quality.
 *
 * Full resolution is comfortable here — under 5ms a frame on this machine —
 * but the moment this layer took over the artwork, a slow GPU stopped meaning
 * "the weather stutters" and started meaning "the hero stutters". So it
 * measures itself: if the median frame is still slow after a warm-up, the
 * backing store steps down. Only ever downward, and only twice, so it settles
 * instead of oscillating.
 */
const SCALE_STEPS = [1, 0.72, 0.5];
let scaleStep = 0;
let sampleFrames = [];
let warmup = 30;

const VERT = `
attribute vec2 a_pos;
void main() { gl_Position = vec4(a_pos, 0.0, 1.0); }
`;

const FRAG = `
precision highp float;

uniform vec2 u_res;
uniform float u_time;
uniform float u_imgAspect;
uniform vec2 u_focus;
uniform vec2 u_objPos;
uniform float u_intensity;
uniform sampler2D u_plate;
uniform sampler2D u_depth;
uniform float u_hasPlate;
uniform float u_hasDepth;
uniform vec2 u_texel;
uniform vec2 u_parallax;

vec3 mod289(vec3 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
vec4 mod289(vec4 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
vec4 permute(vec4 x) { return mod289(((x * 34.0) + 1.0) * x); }
vec4 taylorInvSqrt(vec4 r) { return 1.79284291400159 - 0.85373472095314 * r; }

float snoise(vec3 v) {
  const vec2 C = vec2(1.0 / 6.0, 1.0 / 3.0);
  const vec4 D = vec4(0.0, 0.5, 1.0, 2.0);
  vec3 i = floor(v + dot(v, C.yyy));
  vec3 x0 = v - i + dot(i, C.xxx);
  vec3 g = step(x0.yzx, x0.xyz);
  vec3 l = 1.0 - g;
  vec3 i1 = min(g.xyz, l.zxy);
  vec3 i2 = max(g.xyz, l.zxy);
  vec3 x1 = x0 - i1 + C.xxx;
  vec3 x2 = x0 - i2 + C.yyy;
  vec3 x3 = x0 - D.yyy;
  i = mod289(i);
  vec4 p = permute(permute(permute(
      i.z + vec4(0.0, i1.z, i2.z, 1.0))
    + i.y + vec4(0.0, i1.y, i2.y, 1.0))
    + i.x + vec4(0.0, i1.x, i2.x, 1.0));
  float n_ = 0.142857142857;
  vec3 ns = n_ * D.wyz - D.xzx;
  vec4 j = p - 49.0 * floor(p * ns.z * ns.z);
  vec4 x_ = floor(j * ns.z);
  vec4 y_ = floor(j - 7.0 * x_);
  vec4 x = x_ * ns.x + ns.yyyy;
  vec4 y = y_ * ns.x + ns.yyyy;
  vec4 h = 1.0 - abs(x) - abs(y);
  vec4 b0 = vec4(x.xy, y.xy);
  vec4 b1 = vec4(x.zw, y.zw);
  vec4 s0 = floor(b0) * 2.0 + 1.0;
  vec4 s1 = floor(b1) * 2.0 + 1.0;
  vec4 sh = -step(h, vec4(0.0));
  vec4 a0 = b0.xzyw + s0.xzyw * sh.xxyy;
  vec4 a1 = b1.xzyw + s1.xzyw * sh.zzww;
  vec3 p0 = vec3(a0.xy, h.x);
  vec3 p1 = vec3(a0.zw, h.y);
  vec3 p2 = vec3(a1.xy, h.z);
  vec3 p3 = vec3(a1.zw, h.w);
  vec4 norm = taylorInvSqrt(vec4(dot(p0, p0), dot(p1, p1), dot(p2, p2), dot(p3, p3)));
  p0 *= norm.x; p1 *= norm.y; p2 *= norm.z; p3 *= norm.w;
  vec4 m = max(0.6 - vec4(dot(x0, x0), dot(x1, x1), dot(x2, x2), dot(x3, x3)), 0.0);
  m = m * m;
  return 42.0 * dot(m * m, vec4(dot(p0, x0), dot(p1, x1), dot(p2, x2), dot(p3, x3)));
}

float fbm(vec3 p) {
  float sum = 0.0;
  float amp = 0.5;
  for (int i = 0; i < 4; i++) {
    sum += amp * snoise(p);
    p *= 2.02;
    amp *= 0.5;
  }
  return sum;
}

/* Must stay at or above the largest displacement the parallax can produce,
 * which is the vertical one under a full scroll. */
const float OVERSCAN = 0.018;

float hash(float n) { return fract(sin(n * 12.9898) * 43758.5453); }

/*
 * One candidate strike per window, most of them skipped.
 *
 * A sine or a noise threshold gives evenly spaced flashes, which reads as a
 * blinking light rather than as a storm. Here each window rolls its own dice:
 * whether it fires at all, when inside the window, how hard, and from where.
 * The gaps come out uneven, which is the whole point — you cannot anticipate
 * the next one.
 *
 * Returns the strike's brightness; the origin argument receives where it
 * fired from, in the same aspect-corrected image space as everything else.
 */
/*
 * The shape of one discharge, in seconds since it began.
 *
 * A single exponential is a lamp being switched off. A real flash is a
 * sequence: a faint leader, the main stroke a breath later, one or two return
 * strokes down the same channel, and an afterglow in the cloud that outlasts
 * all of them by an order of magnitude. That stutter is most of what makes a
 * flash legible as lightning rather than as a brightness animation, and the
 * intervals are tens of milliseconds — far too fast to fake with a sine.
 */
float bolt(float x, float seed) {
  if (x < 0.0) return 0.0;

  float e = 0.32 * exp(-x / 0.022);

  float m = x - 0.085;
  e += step(0.0, m) * exp(-m / 0.048);

  float r1 = x - 0.152;
  e += step(0.0, r1) * 0.62 * exp(-r1 / 0.040) * step(0.30, hash(seed + 3.1));

  float r2 = x - 0.238;
  e += step(0.0, r2) * 0.41 * exp(-r2 / 0.034) * step(0.58, hash(seed + 7.7));

  /* The cloud stays hot after the channel dies. Without this the frame snaps
   * back to black and the strike reads as a dropped frame. */
  float g = x - 0.085;
  e += step(0.0, g) * 0.11 * exp(-g / 0.42);

  return e;
}

/*
 * One candidate strike per window, most of them skipped.
 *
 * A sine or a noise threshold gives evenly spaced flashes, which reads as a
 * blinking light rather than as a storm. Here each window rolls its own dice:
 * whether it fires at all, when inside the window, how hard, and from where.
 * The gaps come out uneven, which is the whole point — you cannot anticipate
 * the next one.
 *
 * Power is cubed, so the roll is heavily biased toward faint sheet flashes
 * with the occasional close strike among them. A storm where every bolt hits
 * equally hard reads as a strobe; the rare full blowout only lands because
 * most of its neighbours did not.
 *
 * Returns the strike's brightness; the origin argument receives where it
 * fired from, in the same aspect-corrected image space as everything else.
 */
float strike(float t, float period, float seed, out vec2 origin) {
  float cyc = t / period + seed;
  float n = floor(cyc);
  float f = fract(cyc);

  float roll = hash(n + seed * 37.0);
  float when = 0.06 + hash(n + seed * 91.0) * 0.62;
  float near = hash(n + seed * 53.0);
  float power = mix(0.10, 1.0, near * near * near);

  origin = vec2(0.25 + hash(n + seed * 17.0) * 1.35, 0.10 + hash(n + seed * 71.0) * 0.28);

  return bolt((f - when) * period, n + seed) * power * step(0.58, roll);
}

/* Reproduces CSS object-fit: cover + object-position, so image UV stays
 * pinned to the picture no matter how the element is cropped. */
vec2 coverUV(vec2 uv) {
  float ae = u_res.x / u_res.y;
  vec2 s = vec2(1.0);
  if (ae > u_imgAspect) s.y = u_imgAspect / ae;
  else s.x = ae / u_imgAspect;
  return uv * s + u_objPos * (1.0 - s);
}

void main() {
  vec2 uv = gl_FragCoord.xy / u_res;
  uv.y = 1.0 - uv.y;
  vec2 flat_uv = coverUV(uv);

  /*
   * Parallax.
   *
   * The plate is one flat painting, so depth has to come from somewhere: a
   * monocular depth pass, baked to rift-gate-depth.webp, which separates
   * every figure from the fog behind it. Displacing the sample by that depth
   * moves the near figures further than the distant ones and leaves the sky
   * still — the whole point, since the sky is the one thing that must not
   * swim.
   *
   * This is also why the canvas draws the plate rather than screening light
   * over an <img>. Two layers sliding past each other would need the figures
   * cut out and the hole behind them painted in, and any error there shows up
   * as a ghost of the figure left at its old position. One displaced sample
   * cannot ghost: there is only ever one of everything.
   *
   * The second tap is the fix for depth swim. Displacing by the depth found
   * at the *undisplaced* position drags a near object's depth value out over
   * its neighbours; re-reading depth where the sample actually landed keeps
   * the boundary with the object it belongs to.
   */
  /* Overscan. In a wide viewport the cover crop already reaches both side
   * edges of the plate, so a displaced sample there would clamp and drag the
   * border pixel into a smear. Sampling from fractionally inside the image
   * buys the margin the displacement needs; 1.8% of a photograph is not a
   * zoom anyone can see. */
  flat_uv = mix(vec2(OVERSCAN), vec2(1.0 - OVERSCAN), flat_uv);

  float dep = 0.0;
  if (u_hasDepth > 0.5) {
    dep = texture2D(u_depth, flat_uv).r;
    dep = texture2D(u_depth, flat_uv - u_parallax * dep).r;
  }
  vec2 iuv = clamp(flat_uv - u_parallax * dep, 0.0, 1.0);

  float t = u_time;

  /* Aspect-corrected image space, so the cloud cells stay round rather than
   * stretching with the viewport. */
  vec2 p = vec2(iuv.x * u_imgAspect, iuv.y) * 2.4;
  vec2 f = vec2(u_focus.x * u_imgAspect, u_focus.y);

  /* Domain warp: one noise field displacing another is what turns uniform
   * puffs into something that boils and folds like weather. */
  vec3 wq = vec3(p * 0.75 + vec2(t * 0.030, -t * 0.014), t * 0.10);
  float warp = fbm(wq);
  vec3 cq = vec3(p + warp * 0.75 + vec2(t * 0.055, -t * 0.024), t * 0.16);
  float clouds = fbm(cq);
  /* A hard window, not a gentle ramp: distinct lit wisps against untouched
   * dark are what read as movement. A soft curve raises the whole sky evenly
   * and just looks like a brighter still. */
  clouds = smoothstep(0.05, 0.62, clouds);

  /* Fine detail rides on top at a faster tempo — the difference between fog
   * sliding past and cloud actually churning. */
  float detail = fbm(vec3(p * 2.7 + vec2(t * 0.11, -t * 0.05), t * 0.34));
  clouds *= 0.55 + 0.65 * smoothstep(-0.25, 0.65, detail);

  /* The storm only lives above the skyline; below it the frame is wet ground
   * and the page's own text. */
  float sky = smoothstep(0.66, 0.28, iuv.y);
  sky *= smoothstep(0.02, 0.16, iuv.y);

  /* Distance to the rift, in the same aspect-corrected space. */
  float d = distance(vec2(iuv.x * u_imgAspect, iuv.y), f);

  /* Two breathing rates that never line up, so the pulse never reads as a
   * loop. */
  float pulse = 0.74 + 0.20 * sin(t * 0.55) + 0.10 * sin(t * 0.31 + 1.7);

  /* Rare, sharp surges along the rift — the discharge already drawn into the
   * plate, given a reason to flicker. */
  float surge = snoise(vec3(11.3, 4.7, t * 0.42));
  surge = pow(max(surge, 0.0), 7.0);

  /* The rift lights the whole sky, not a circle around itself — the falloff
   * is gentle and keeps a floor, so cloud at the frame's edge still catches
   * enough to show it is moving. */
  float lit = 0.22 + 0.78 * exp(-d * 1.5);
  float halo = exp(-d * 4.6);

  vec3 teal = vec3(0.16, 1.00, 0.80);
  vec3 violet = vec3(0.58, 0.44, 1.00);
  /* The plate's own two-colour lighting: violet at the ring, teal spilling
   * outward through the cloud. */
  vec3 tint = mix(teal, violet, smoothstep(0.30, 0.02, d) * 0.85);

  /* Two generators on periods that share no common multiple, so the storm
   * never settles into a countable rhythm. */
  vec2 oA, oB;
  float flash = strike(t, 7.3, 0.0, oA) + strike(t, 11.9, 0.37, oB) * 0.8;
  vec2 flashPos = mix(oA, oB, 0.5);

  /* The standing weather: always there, always moving. */
  vec3 standing = vec3(0.0);
  standing += tint * clouds * sky * lit * 0.52 * pulse;
  standing += tint * halo * pulse * 0.30;
  standing += violet * surge * (0.30 + 0.70 * clouds) * sky * lit * 1.5;
  standing += teal * surge * halo * 0.9;

  /*
   * The splash, lit the way a real strike lights a landscape.
   *
   * The discharge is behind the cloud, so the sky is the light source and
   * everything in front of it is backlit. That means the sky blows out toward
   * white while the ruins, the ground and the figures get only a weak bounce
   * — and it is precisely that gap that reads as lightning. Lifting the whole
   * frame evenly, which is the obvious thing to do, produces a brightness
   * animation instead: the foreground goes grey and the silhouettes dissolve.
   *
   * The light is near-white, faintly blue. Screening white over the plate
   * washes its colour out on the way up, which is the desaturation a real
   * flash produces and the reason the shapes go monochrome for that instant.
   */
  float reach = 0.46 + 0.54 * exp(-distance(vec2(iuv.x * u_imgAspect, iuv.y), flashPos) * 1.0);
  vec3 flashCol = vec3(0.90, 0.96, 1.00);

  /* Cloud shapes the flash but never gates it out — an unlit patch of sky
   * still has to blow, or the strike looks like it is lighting a texture. */
  /* Its own sky mask, without the top-edge fade the standing glow needs: a
   * strike has to blow the whole sky, right to the top of the frame. */
  float skyBand = smoothstep(0.72, 0.30, iuv.y);
  float skyLit = skyBand * (0.52 + 0.48 * clouds);
  /* Depth knows what is a figure and what is dark cloud; the geometric band
   * only ever knew what was low in the frame. Where depth is available it
   * decides, because a strike lighting the underside of a cloud as though it
   * were wet ground is the tell that this is a gradient and not a scene. */
  float ground = u_hasDepth > 0.5 ? smoothstep(0.04, 0.24, dep) : (1.0 - skyBand);

  vec3 splash = flashCol * flash * reach * skyLit * 2.10;

  /*
   * What the strike does to the ground, read off the plate itself.
   *
   * A blind term can only raise the foreground uniformly, which is fog, not
   * light. Sampling the artwork tells the shader where the figures, the
   * ruins and the wet ground actually are, and light can then behave the way
   * it does in the world:
   *
   *   Fill — screening white lifts a dark pixel far more than a light one, so
   *   weighting by darkness on top of that opens the shadows up rather than
   *   washing the whole plane. This is what makes the shadows go away for the
   *   instant the sky is lit.
   *
   *   Rim — the bright edge that appears on anything standing between you and
   *   a light. A luminance gradient across the plate finds exactly those
   *   boundaries: the shoulders and hoods of the figures, the tops of the
   *   ruins, the lip of every puddle. Weighted to edges whose bright side
   *   faces the flash, so the highlight lands on the side being lit and not
   *   all the way around the outline.
   */
  if (u_hasPlate > 0.5 && flash > 0.001) {
    vec3 plate = texture2D(u_plate, iuv).rgb;
    float lum = dot(plate, vec3(0.2126, 0.7152, 0.0722));

    vec2 e = u_texel * 1.4;
    float lx =
      dot(texture2D(u_plate, iuv + vec2(e.x, 0.0)).rgb, vec3(0.2126, 0.7152, 0.0722)) -
      dot(texture2D(u_plate, iuv - vec2(e.x, 0.0)).rgb, vec3(0.2126, 0.7152, 0.0722));
    float ly =
      dot(texture2D(u_plate, iuv + vec2(0.0, e.y)).rgb, vec3(0.2126, 0.7152, 0.0722)) -
      dot(texture2D(u_plate, iuv - vec2(0.0, e.y)).rgb, vec3(0.2126, 0.7152, 0.0722));

    /* Light arrives from the flash, so an edge is rimmed when its bright side
     * points back at it. */
    vec2 toLight = normalize(flashPos - vec2(iuv.x * u_imgAspect, iuv.y) + 1e-5);
    float facing = clamp(dot(normalize(vec2(lx, ly) + 1e-5), toLight), 0.0, 1.0);
    /* Only the lit side. A generous floor here rims both sides of every
     * object at once, which stops being light and becomes an outline filter
     * — the giveaway is cloud texture coming back edge-traced. */
    float edge = smoothstep(0.02, 0.22, length(vec2(lx, ly)));
    float rim = edge * facing * facing * (1.0 - smoothstep(0.22, 0.62, lum));

    float fill = pow(1.0 - clamp(lum * 1.6, 0.0, 1.0), 1.7);

    splash += flashCol * flash * reach * ground * (fill * 0.66 + rim * 1.05);
    /* The skyline keeps its rims too — the ruins are the far edge of the
     * ground, and cutting them at the horizon draws a line across the frame.
     * Weak, because up here the same term starts finding cloud. */
    splash += flashCol * flash * reach * skyBand * rim * 0.20;
  }

  /*
   * Two different handovers back to the artwork.
   *
   * The standing glow stops above the type — words must never sit inside
   * something that is slowly moving, or they crawl. A strike is over in a
   * fraction of a second and reads as the scene being lit rather than as
   * motion behind the text, so it is allowed most of the way down.
   */
  float guard = smoothstep(0.86, 0.52, uv.y);
  float flashGuard = smoothstep(1.0, 0.62, uv.y);

  /* The plate is the base now, not a backdrop this layer screens onto. */
  vec3 plateCol = u_hasPlate > 0.5 ? texture2D(u_plate, iuv).rgb : vec3(0.0);
  vec3 light = (standing * guard + splash * flashGuard) * u_intensity;

  /* Screen, done here rather than by the compositor — same curve as the CSS
   * blend mode this replaced, so the light still lifts the darks hardest and
   * can never drive a highlight past white. */
  gl_FragColor = vec4(1.0 - (1.0 - plateCol) * (1.0 - clamp(light, 0.0, 1.0)), 1.0);
}
`;

function compile(ctx, type, source) {
  const shader = ctx.createShader(type);
  ctx.shaderSource(shader, source);
  ctx.compileShader(shader);
  if (!ctx.getShaderParameter(shader, ctx.COMPILE_STATUS)) {
    console.warn('[HeroAtmosphere]', ctx.getShaderInfoLog(shader));
    ctx.deleteShader(shader);
    return null;
  }
  return shader;
}

function upload(image, unit) {
  const ctx = gl.value;
  const tex = ctx.createTexture();
  ctx.activeTexture(unit === 0 ? ctx.TEXTURE0 : ctx.TEXTURE1);
  ctx.bindTexture(ctx.TEXTURE_2D, tex);
  /* Non-power-of-two sources: clamped and unmipmapped, or WebGL 1 silently
   * samples black. */
  ctx.texParameteri(ctx.TEXTURE_2D, ctx.TEXTURE_WRAP_S, ctx.CLAMP_TO_EDGE);
  ctx.texParameteri(ctx.TEXTURE_2D, ctx.TEXTURE_WRAP_T, ctx.CLAMP_TO_EDGE);
  ctx.texParameteri(ctx.TEXTURE_2D, ctx.TEXTURE_MIN_FILTER, ctx.LINEAR);
  ctx.texParameteri(ctx.TEXTURE_2D, ctx.TEXTURE_MAG_FILTER, ctx.LINEAR);
  ctx.texImage2D(ctx.TEXTURE_2D, 0, ctx.RGBA, ctx.RGBA, ctx.UNSIGNED_BYTE, image);
  return tex;
}

function loadImage(src) {
  return new Promise((resolve) => {
    if (!src) return resolve(null);
    const image = new Image();
    image.decoding = 'async';
    image.onload = () => resolve(image);
    image.onerror = () => resolve(null);
    image.src = src;
  });
}

/*
 * Depth is an enhancement, never a dependency: without it u_hasDepth stays 0,
 * the parallax is skipped and the lighting falls back to the geometric bands.
 * The plate is different — this layer is opaque now, so it stays hidden until
 * the plate is up and the <img> keeps the hero.
 */
async function loadTextures() {
  if (!gl.value || !props.plate) return;

  const [plateImg, depthImg] = await Promise.all([loadImage(props.plate), loadImage(props.depth)]);

  const ctx = gl.value;
  if (!ctx || !program || !plateImg) return;

  ctx.useProgram(program);
  plateTex = upload(plateImg, 0);
  ctx.uniform1f(uniforms.hasPlate, 1);
  ctx.uniform2f(uniforms.texel, 1 / plateImg.naturalWidth, 1 / plateImg.naturalHeight);

  if (depthImg) {
    depthTex = upload(depthImg, 1);
    ctx.uniform1f(uniforms.hasDepth, 1);
  }

  ready.value = true;
  draw();
}

function onPointerMove(event) {
  if (reduced) return;
  /* Offset from the middle of the window, not of the hero: the hero is the
   * full viewport here, and reading it per event would force layout. */
  target.x = (event.clientX / window.innerWidth - 0.5) * 2;
  target.y = (event.clientY / window.innerHeight - 0.5) * 2;
}

function onPointerLeave() {
  target.x = 0;
  target.y = 0;
}

/* Scroll drives it too. A phone has no pointer, and without this the whole
 * effect would simply not exist on the devices most likely to see the page. */
function onScroll() {
  scrollShift = Math.min(window.scrollY / Math.max(1, window.innerHeight), 1);
}

function resize() {
  const ctx = gl.value;
  const el = canvas.value;
  if (!ctx || !el) return;
  /* Layout size, not the bounding rect: the element carries the hero's
   * push-in transform, and measuring the transformed box would rebuild the
   * backing store on every frame of that animation. */
  const dpr = Math.min(window.devicePixelRatio || 1, 2);
  const w = Math.max(1, Math.round(el.offsetWidth * dpr * renderScale));
  const h = Math.max(1, Math.round(el.offsetHeight * dpr * renderScale));
  if (el.width === w && el.height === h) return;
  el.width = w;
  el.height = h;
  ctx.viewport(0, 0, w, h);
}

function draw() {
  const ctx = gl.value;
  if (!ctx) return;
  resize();
  ctx.uniform2f(uniforms.res, ctx.drawingBufferWidth, ctx.drawingBufferHeight);
  ctx.uniform1f(uniforms.time, clock);
  /* Divided by the aspect on x so a given pointer movement shifts the scene
   * by the same distance horizontally and vertically. */
  ctx.uniform2f(
    uniforms.parallax,
    (current.x * props.parallax) / props.imageAspect,
    current.y * props.parallax
  );
  ctx.drawArrays(ctx.TRIANGLES, 0, 3);
}

function loop(now) {
  frame = requestAnimationFrame(loop);
  if (!visible) {
    last = now;
    return;
  }
  /* Advancing an explicit clock rather than reading wall time means a tab that
   * was backgrounded for ten minutes resumes where it paused instead of
   * jumping the weather forward. */
  const dt = last ? Math.min((now - last) / 1000, 0.1) : 0;
  last = now;
  clock += dt;

  /* Frame-rate independent easing, so the parallax lags the pointer by the
   * same amount of time on a 60Hz panel and a 120Hz one. */
  const k = 1 - Math.pow(0.0025, dt);
  current.x += (target.x - current.x) * k;
  /* Scrolling pushes the near planes down as the hero leaves, which is the
   * direction they would move if the camera were rising past them. Bounded,
   * because OVERSCAN in the shader is sized against exactly this number. */
  const aim = Math.max(-1.6, Math.min(1.6, target.y + scrollShift * 1.4));
  current.y += (aim - current.y) * k;

  measure(dt);
  draw();
}

function measure(dt) {
  if (scaleStep >= SCALE_STEPS.length - 1 || !dt) return;
  if (warmup > 0) {
    warmup -= 1;
    return;
  }
  sampleFrames.push(dt);
  if (sampleFrames.length < 60) return;

  const median = sampleFrames.sort((a, b) => a - b)[30];
  sampleFrames = [];
  warmup = 30;

  /* 20ms is below 50fps. One slow frame is a garbage collection; a slow
   * median over a full second is a GPU that cannot carry this. */
  if (median > 0.02) {
    scaleStep += 1;
    renderScale = SCALE_STEPS[scaleStep];
    const el = canvas.value;
    /* resize() early-returns unless the target size actually changed, so the
     * backing store has to be invalidated for the new scale to take. */
    if (el) el.width = 0;
    resize();
  }
}

function onVisibility() {
  visible = inView && !document.hidden;
}

/*
 * A dead context must never stay on screen.
 *
 * Chrome composites a canvas whose context has been lost as opaque WHITE, and
 * this one is stretched over the whole hero with the figure bands stacked
 * above it — so the loss reads as a white page with black cut-outs standing on
 * it. That is the flash people were seeing, and the context is lost for
 * ordinary reasons: the browser caps how many live WebGL contexts a page may
 * hold and evicts the oldest when a new one is created, which is exactly what
 * happens when the dice chamber spins its renderer up.
 *
 * preventDefault() is what makes a restore possible at all; until one arrives
 * the <img> underneath carries the hero, which is the same state as a machine
 * with no WebGL. Hidden through `display`, not the opacity class, because a
 * 220ms fade out of white is still a flash of white.
 */
function onContextLost(event) {
  event.preventDefault();
  cancelAnimationFrame(frame);
  ready.value = false;
  if (canvas.value) canvas.value.style.display = 'none';
}

function onContextRestored() {
  const el = canvas.value;
  if (!el) return;
  el.style.display = '';
  /* The GPU kept nothing: program, buffer and textures all have to be rebuilt,
   * and that is everything onMounted does. */
  init();
}

/* Everything the GPU owns. Split out of onMounted so a context restore can
 * rebuild it — a restored context comes back empty. */
function init() {
  const el = canvas.value;
  if (!el) return;

  const ctx =
    el.getContext('webgl', {
      alpha: true,
      antialias: false,
      depth: false,
      premultipliedAlpha: true,
      /*
       * Required, not an optimisation knob. Without it the drawing buffer is
       * undefined after each composite, so any repaint that is not preceded by
       * a draw shows black — and this layer stops redrawing whenever it is not
       * animating: a backgrounded tab, and every frame under reduced motion,
       * which draws exactly once. Harmless while this was additive light over
       * an <img>; now that it carries the artwork it blacks the hero out.
       */
      preserveDrawingBuffer: true,
    }) ||
    el.getContext('experimental-webgl');
  if (!ctx) {
    supported.value = false;
    return;
  }
  gl.value = ctx;

  const vs = compile(ctx, ctx.VERTEX_SHADER, VERT);
  const fs = compile(ctx, ctx.FRAGMENT_SHADER, FRAG);
  if (!vs || !fs) {
    supported.value = false;
    return;
  }

  program = ctx.createProgram();
  ctx.attachShader(program, vs);
  ctx.attachShader(program, fs);
  ctx.linkProgram(program);
  if (!ctx.getProgramParameter(program, ctx.LINK_STATUS)) {
    console.warn('[HeroAtmosphere]', ctx.getProgramInfoLog(program));
    supported.value = false;
    return;
  }
  ctx.useProgram(program);

  const buffer = ctx.createBuffer();
  ctx.bindBuffer(ctx.ARRAY_BUFFER, buffer);
  ctx.bufferData(ctx.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), ctx.STATIC_DRAW);
  const loc = ctx.getAttribLocation(program, 'a_pos');
  ctx.enableVertexAttribArray(loc);
  ctx.vertexAttribPointer(loc, 2, ctx.FLOAT, false, 0, 0);

  uniforms = {
    res: ctx.getUniformLocation(program, 'u_res'),
    time: ctx.getUniformLocation(program, 'u_time'),
    imgAspect: ctx.getUniformLocation(program, 'u_imgAspect'),
    focus: ctx.getUniformLocation(program, 'u_focus'),
    objPos: ctx.getUniformLocation(program, 'u_objPos'),
    intensity: ctx.getUniformLocation(program, 'u_intensity'),
    plate: ctx.getUniformLocation(program, 'u_plate'),
    depth: ctx.getUniformLocation(program, 'u_depth'),
    hasPlate: ctx.getUniformLocation(program, 'u_hasPlate'),
    hasDepth: ctx.getUniformLocation(program, 'u_hasDepth'),
    texel: ctx.getUniformLocation(program, 'u_texel'),
    parallax: ctx.getUniformLocation(program, 'u_parallax'),
  };
  ctx.uniform1i(uniforms.plate, 0);
  ctx.uniform1i(uniforms.depth, 1);
  ctx.uniform1f(uniforms.hasPlate, 0);
  ctx.uniform1f(uniforms.hasDepth, 0);
  ctx.uniform2f(uniforms.texel, 1 / 1456, 1 / 816);
  ctx.uniform2f(uniforms.parallax, 0, 0);
  ctx.uniform1f(uniforms.imgAspect, props.imageAspect);
  ctx.uniform2f(uniforms.focus, props.focus[0], props.focus[1]);
  ctx.uniform2f(uniforms.objPos, props.objectPosition[0], props.objectPosition[1]);
  ctx.uniform1f(uniforms.intensity, props.intensity);

  loadTextures();

  /* Draw once immediately. Waiting for the first animation frame leaves the
   * canvas at its default 300x150 until then, and a tab opened in the
   * background never gets that frame at all. */
  clock = 12;
  draw();

  /* One frame at a non-zero clock is the whole effect under reduced motion:
   * the rift's bloom and the lit cloud edges, with nothing moving. */
  if (reduced) return;
  frame = requestAnimationFrame(loop);
}

onMounted(() => {
  reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;

  const el = canvas.value;
  if (!el) return;

  /* Registered before init and independently of it, because the context can be
   * lost at any moment — including one this component never asked for, when
   * another page's renderer pushes it out of the browser's context budget. */
  el.addEventListener('webglcontextlost', onContextLost);
  el.addEventListener('webglcontextrestored', onContextRestored);

  init();
  if (!gl.value || reduced) return;

  observer = new IntersectionObserver(
    ([entry]) => {
      inView = entry.isIntersecting;
      onVisibility();
    },
    { threshold: 0 }
  );
  observer.observe(el);
  document.addEventListener('visibilitychange', onVisibility);
  window.addEventListener('resize', draw);
  /*
   * With parallax at 0 there is nothing for the pointer or the scroll position
   * to drive, so the listeners are not registered at all rather than left
   * running to compute an offset that gets multiplied by zero. A pointermove
   * handler on window is not free.
   */
  if (props.parallax > 0) {
    window.addEventListener('pointermove', onPointerMove, { passive: true });
    window.addEventListener('pointerleave', onPointerLeave);
    window.addEventListener('scroll', onScroll, { passive: true });
  }
});

onBeforeUnmount(() => {
  cancelAnimationFrame(frame);
  observer?.disconnect();
  document.removeEventListener('visibilitychange', onVisibility);
  window.removeEventListener('resize', draw);
  window.removeEventListener('pointermove', onPointerMove);
  window.removeEventListener('pointerleave', onPointerLeave);
  window.removeEventListener('scroll', onScroll);
  const el = canvas.value;
  el?.removeEventListener('webglcontextlost', onContextLost);
  el?.removeEventListener('webglcontextrestored', onContextRestored);
  const ctx = gl.value;
  if (ctx) {
    if (plateTex) ctx.deleteTexture(plateTex);
    if (depthTex) ctx.deleteTexture(depthTex);
    plateTex = null;
    depthTex = null;
    /* Hidden BEFORE the context dies. Leaving this route is the commonest way
     * to kill the context, and a lost context composites white — so the order
     * of these two lines is the difference between the hero dissolving and the
     * hero flashing white on its way out. */
    if (el) el.style.display = 'none';
    ctx.getExtension('WEBGL_lose_context')?.loseContext();
    gl.value = null;
  }
});
</script>

<style scoped>
/*
 * This layer carries the artwork itself, so it is opaque and composites
 * normally. The screen blend it used to lean on now happens inside the
 * shader, where it belongs: the light has to be applied to the plate at the
 * displaced position, and a CSS blend mode can only ever combine this layer
 * with whatever the compositor finds underneath it.
 */
.atmosphere {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  display: block;
  pointer-events: none;
  opacity: 0;
}

/* Cross-fades over the identical <img> beneath, so the handover is invisible
 * rather than a pop. */
.atmosphere--live {
  opacity: 1;
  transition: opacity 220ms var(--ease);
}
</style>
